import React, { useState, useEffect } from 'react';
import { CreditCard, Download, Calendar, DollarSign, Users, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSchool } from '../../contexts/SchoolContext';
import { SubscriptionService } from '../../services/SubscriptionService';
import { PaymentHistory, BillingInfo } from '../../types/subscription';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import UpgradeModal from './UpgradeModal';
import BillingInfoForm from './BillingInfoForm';
import AppLayout from '../layout/AppLayout';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { isTestMode } from '../../config/stripe';

const BillingDashboard: React.FC = () => {
  useDocumentTitle('Billing');
  
  const { subscription, currentPlan, loading, manageSubscription } = useSubscription();
  const { selectedSchool: school } = useSchool();
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (school?.id) {
      loadPaymentHistory();
      loadBillingInfo();
    }
  }, [school?.id]);

  const loadPaymentHistory = async () => {
    if (!school?.id) return;
    
    try {
      setLoadingHistory(true);
      const history = await SubscriptionService.getPaymentHistory(school.id);
      setPaymentHistory(history);
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadBillingInfo = async () => {
    if (!school?.id) return;
    
    try {
      const info = await SubscriptionService.getBillingInfo(school.id);
      setBillingInfo(info);
    } catch (error) {
      console.error('Error loading billing info:', error);
    }
  };

  const handleDownloadInvoice = async (paymentId: string) => {
    try {
      const response = await fetch(`/api/download-invoice/${paymentId}`);
      if (!response.ok) throw new Error('Failed to download invoice');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'past_due': return 'text-yellow-600 bg-yellow-100';
      case 'canceled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Stripe Mode Indicator */}
        {!isTestMode() && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Live Payment Mode Active</p>
              <p className="text-xs text-red-600">Real charges will occur. Please be careful when making payments.</p>
            </div>
          </div>
        )}
        
        {isTestMode() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center space-x-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Test Mode Active</p>
              <p className="text-xs text-blue-600">You can safely test payments. No real charges will occur.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowBillingForm(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Update Billing Info
          </button>
          {currentPlan?.id === 'free' && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Current Plan Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
          {subscription && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
              {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center">
            <CreditCard className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Plan</p>
              <p className="text-xl font-semibold text-gray-900">{currentPlan?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Monthly Cost</p>
              {currentPlan?.price === 0 ? (
                <p className="text-xl font-semibold text-gray-900">Free</p>
              ) : (
                <div>
                  <p className="text-xl font-semibold text-gray-900">
                    ${currentPlan?.price || 0}/user/month
                  </p>
                  {subscription?.userCount && (
                    <p className="text-sm text-gray-600">
                      {subscription.userCount} users × ${currentPlan?.price || 0} = ${(subscription.userCount * (currentPlan?.price || 0))}/month
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Users</p>
              <p className="text-xl font-semibold text-gray-900">
                {subscription?.userCount || currentPlan?.maxUsers || 'Unlimited'}
              </p>
              {subscription?.userCount && (
                <p className="text-sm text-gray-600">Subscribed users</p>
              )}
            </div>
          </div>
        </div>

        {subscription && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Next billing date</p>
                <p className="text-lg font-medium text-gray-900">
                  {format(subscription.currentPeriodEnd, 'MMM d, yyyy')}
                </p>
              </div>
              {subscription.cancelAtPeriodEnd && (
                <div className="text-right">
                  <p className="text-sm text-red-600">Canceling at period end</p>
                  <button
                    onClick={() => {/* TODO: Implement reactivation */}}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Reactivate
                  </button>
                </div>
              )}
            </div>
            
            {/* Manage Subscription Button */}
            {subscription && subscription.status === 'active' && (
              <div className="mt-4">
                <button
                  onClick={manageSubscription}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Manage Subscription
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Update payment method, change plan, or cancel subscription
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentPlan?.features.map((feature, index) => (
            <div key={index} className="flex items-center">
              <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
          <TrendingUp className="h-5 w-5 text-gray-400" />
        </div>
        
        {loadingHistory ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : paymentHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No payment history available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(payment.createdAt, 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(payment.amount / 100).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payment.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.status === 'succeeded' && (
                        <button
                          onClick={() => handleDownloadInvoice(payment.id)}
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={currentPlan}
        />
      )}
      
      {showBillingForm && (
        <BillingInfoForm
          isOpen={showBillingForm}
          onClose={() => setShowBillingForm(false)}
          initialData={billingInfo}
          onSave={loadBillingInfo}
        />
      )}
      </div>
    </AppLayout>
  );
};

export default BillingDashboard;